import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebase";
import { 
  LayoutDashboardIcon, 
  ClipboardListIcon, 
  SparklesIcon, 
  UserCircleIcon, 
  MenuIcon, 
  XIcon,
  HeartPulseIcon 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });

    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      unsubscribe();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const userName = user ? user.displayName : "Guest";

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const NavButton = ({ icon, text, onClick }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 transition-all duration-300 ease-in-out"
    >
      {icon}
      <span className="text-sm font-medium">{text}</span>
    </motion.button>
  );

  return (
    <motion.header 
      ref={headerRef}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gray-800 text-gray-100 py-4 shadow-xl sticky top-0 z-50"
    >
      <div className="container mx-auto flex justify-between items-center px-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/")}
          className="text-2xl font-bold tracking-tight text-teal-400 focus:outline-none"
        >
          Health<span className="text-cyan-400">AI</span>
        </motion.button>

        <motion.button
          whileHover={{ rotate: 90 }}
          className="text-gray-100 text-2xl md:hidden focus:outline-none"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <XIcon /> : <MenuIcon />}
        </motion.button>

        <AnimatePresence>
          {user && (
            <nav 
              className={`${
                menuOpen ? "block" : "hidden"
              } absolute top-full left-0 w-full bg-gray-800 md:static md:block md:w-auto`}
            >
              <motion.ul 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col md:flex-row md:space-x-4 items-center py-4 md:py-0"
              >
                <li><NavButton 
                  icon={<LayoutDashboardIcon className="text-teal-400" />} 
                  text="Health Journal" 
                  onClick={() => navigate("/dashboard")} 
                /></li>
                <li><NavButton 
                  icon={<ClipboardListIcon className="text-cyan-400" />} 
                  text="Meal Tracker" 
                  onClick={() => navigate("/food")} 
                /></li>

               {/*  <li><NavButton 
                  icon={<SparklesIcon className="text-purple-400" />} 
                  text="AI Recommendations" 
                  onClick={() => navigate("/recommendations")} 
                /></li> */}
                
                <li><NavButton 
                  icon={<HeartPulseIcon className="text-indigo-400" />} 
                  text="Health Profile" 
                  onClick={() => navigate("/profile-setup")} 
                /></li>
                <li className="relative">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center gap-2"
                  >
                    <button 
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="flex items-center gap-2 hover:text-teal-400 transition"
                    >
                      <UserCircleIcon className="text-gray-300" />
                      <span className="text-sm font-medium">{userName}</span>
                    </button>
                  </motion.div>

                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-12 right-0 bg-gray-700 text-gray-100 shadow-2xl rounded-lg py-2 w-48 border border-gray-600"
                      >
                        <button
                          onClick={() => navigate("/profile")}
                          className="block w-full text-left px-4 py-2 hover:bg-gray-600 transition flex items-center gap-2"
                        >
                          <UserCircleIcon size={16} /> Profile
                        </button>
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 hover:bg-gray-600 transition flex items-center gap-2 text-red-400"
                        >
                          <XIcon size={16} /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              </motion.ul>
            </nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;